import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import IosShareIcon from "@mui/icons-material/IosShare";
import Mosqueimg from "../../assets/audio/images/mosqueaudio.jpg";
import NavbarAll from "../NavbarAll/NavbarAllFiles";
import CustomAudioPlayer from "./CustomAudioPlayer";
import "./BayanListPage.css";

const websiteUrl = "https://www.zakariamasjid.com";

const BayanListPage = ({ apiType, pageTitle, pageSubtitle, countPerPage = 10 }) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const [isLoading, setIsLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [bayanCount, setBayanCount] = useState(0);
  const [bayans, setBayans] = useState([]);
  const [invalidPages, setInvalidPages] = useState([]);

  useEffect(() => {
    const fetchBayans = async () => {
      setIsLoading(true);
      try {
        const [countResponse, listResponse] = await Promise.all([
          axios.get(`${apiUrl}/get-recordings-count/${apiType}`),
          axios.get(`${apiUrl}/get-recordings/${apiType}`, {
            params: { page: pageNumber, limit: countPerPage },
          }),
        ]);

        const nextCount = countResponse.data?.bayantCount || 0;
        const nextBayans = Array.isArray(listResponse.data) ? listResponse.data : [];

        // If count says records exist but current page is empty (e.g. stale count/deletions),
        // shrink count and step back so invalid trailing page buttons disappear immediately.
        if (nextCount > 0 && nextBayans.length === 0 && pageNumber > 1) {
          setBayans([]);
          setBayanCount((pageNumber - 1) * countPerPage);
          setInvalidPages((prev) => (prev.includes(pageNumber) ? prev : [...prev, pageNumber]));
          setPageNumber((prev) => Math.max(1, prev - 1));
          return;
        }

        setBayanCount(nextCount);
        setBayans(nextBayans);
      } catch (error) {
        setBayans([]);
        setBayanCount(0);
        console.error(`Error fetching ${apiType} bayans:`, error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBayans();
  }, [apiType, apiUrl, pageNumber, countPerPage]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pageNumber]);

  useEffect(() => {
    setInvalidPages([]);
  }, [apiType]);

  const totalPages = useMemo(() => {
    const pagesFromCount = Math.max(1, Math.ceil(bayanCount / countPerPage));
    let maxValidPage = pagesFromCount;

    while (maxValidPage > 1 && invalidPages.includes(maxValidPage)) {
      maxValidPage -= 1;
    }

    return maxValidPage;
  }, [bayanCount, countPerPage, invalidPages]);

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === pageNumber) {
      return;
    }
    setPageNumber(nextPage);
  };

  const handleShare = async (bayan) => {
    const bayanId = bayan.id || bayan._id || "shared";
    const query = new URLSearchParams({
      title: bayan.title || "",
      date: bayan.date || "",
      audio: bayan.downloadURL || "",
    }).toString();
    const shareUrl = `${websiteUrl}/share/bayan/${bayanId}?${query}`;
    const sharePayload = {
      title: bayan.title,
      text: `Listen to this bayan: ${bayan.title} ${bayan.date}`,
      url: shareUrl,
    };

    try {
      if (navigator.share) {
        await navigator.share(sharePayload);
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        alert("Bayan link copied to clipboard.");
        return;
      }

      alert("Sharing is not supported in this browser.");
    } catch (error) {
      console.error("Error sharing bayan:", error);
    }
  };

  const getPaginationItems = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages = new Set([1, totalPages, pageNumber - 1, pageNumber, pageNumber + 1]);
    const validPages = [...pages].filter((p) => p > 0 && p <= totalPages).sort((a, b) => a - b);
    const items = [];

    for (let i = 0; i < validPages.length; i += 1) {
      if (i > 0 && validPages[i] - validPages[i - 1] > 1) {
        items.push("ellipsis");
      }
      items.push(validPages[i]);
    }

    return items;
  };

  const paginationItems = getPaginationItems();

  return (
    <>
      {isLoading && (
        <div className="bayan-loader-overlay">
          <div className="bayan-spinner" />
          <p>Loading Bayanats...</p>
        </div>
      )}

      <NavbarAll />

      <section>
        <div className="w-100 pt-80 black-layer pb-70 opc65 position-relative bayan-hero-wrap">
          <div className="fixed-bg" style={{ backgroundImage: `url(${Mosqueimg})` }} />
          <div className="container">
            <div className="page-title-wrap text-center w-100">
              <div className="page-title-inner d-inline-block bayan-hero-card">
                <p className="bayan-hero-kicker">Zakaria Masjid Bayanats</p>
                <h1 className="mb-0">{pageTitle}</h1>
                <p className="bayan-hero-desc">Listen, reflect, and stay connected with our latest recordings.</p>
                <ol className="breadcrumb mb-0 bayan-hero-breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/" title="Home">
                      Masjid - Home
                    </Link>
                  </li>
                  <li className="breadcrumb-item active">{pageSubtitle}</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="w-100 pt-120 pb-120 position-relative">
          <div className="container">
            <div className="bayan-list-wrap">
              {bayans.length === 0 && !isLoading ? (
                <div className="bayan-empty-state">
                  <h4>No recordings found</h4>
                  <p>Please check back shortly for new bayan uploads.</p>
                </div>
              ) : (
                bayans.map((bayan) => (
                  <div key={bayan.id || bayan.downloadURL} className="bayan-card">
                    <div className="bayan-card-head">
                      <div>
                        <p className="bayan-label">Topic</p>
                        <h4>{bayan.title}</h4>
                      </div>
                      <div className="bayan-card-actions">
                        <div className="bayan-date-badge">{bayan.date}</div>
                        <button
                          className="bayan-share-btn"
                          onClick={() => handleShare(bayan)}
                          aria-label="Share bayan"
                        >
                          <IosShareIcon fontSize="small" />
                        </button>
                      </div>
                    </div>

                    <CustomAudioPlayer src={bayan.downloadURL} />
                  </div>
                ))
              )}
            </div>

            {bayans.length > 0 && (
              <div className="bayan-pagination-wrap">
                <button
                  className="bayan-page-btn"
                  onClick={() => handlePageChange(pageNumber - 1)}
                  disabled={pageNumber === 1}
                >
                  Previous
                </button>

                {paginationItems.map((item, index) =>
                  item === "ellipsis" ? (
                    <span key={`ellipsis-${index}`} className="bayan-page-ellipsis">
                      ...
                    </span>
                  ) : (
                    <button
                      key={item}
                      className={`bayan-page-btn ${item === pageNumber ? "active" : ""}`}
                      onClick={() => handlePageChange(item)}
                    >
                      {item}
                    </button>
                  )
                )}

                <button
                  className="bayan-page-btn"
                  onClick={() => handlePageChange(pageNumber + 1)}
                  disabled={pageNumber === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default BayanListPage;
